import { getJson, postJson, requestJson, requestVoid } from './client';

// --- Types ---

export type ReservationAnswerRequest = {
  fieldId: string;
  title: string;
  value: string;
  sortOrder: number;
};

export type CancelBookingRequest = {
  cancelReason: string;
  quoteId?: string;
};

export type ConfirmPaymentRequest = {
  paymentKey: string;
  orderId: string;
  amount: string;
};

export type BookingCommandResponse = {
  bookingId: string;
  status: string;
  expiresAt: string | null;
  totalPrice: number;
  cancelledAt: string | null;
  orderId: string | null;
  paymentId: string | null;
  paidAmount: number | null;
  discountWon: number | null;
};

export type BookingDetailResponse = {
  bookingId: string;
  studioId?: string | null;
  roomId: string;
  roomName: string;
  studioName: string;
  studioAddress?: string | null;
  studioThumbnailUrl?: string | null;
  status: string;
  startsAt: string;
  endsAt: string;
  totalPrice: number;
  paidAmount?: number | null;
  couponDiscountAmount?: number | null;
  paymentMethod: string | null;
  bookerName: string;
  bookerPhone: string;
  bookerNote: string | null;
  reservationAnswers?: ReservationAnswerRequest[];
  selectedOptions?: Array<{
    optionId: string;
    name: string;
    description?: string | null;
    unitPrice?: number | null;
    quantity?: number | null;
    lineTotal?: number | null;
    imageUrl?: string | null;
    sortOrder: number;
  }>;
  refundAmount?: number | null;
  refundedAt?: string | null;
  cancelledAt?: string | null;
  cancelReason: string | null;
  createdAt: string;
};

export type MyBookingItem = {
  bookingId: string;
  studioId: string;
  studioName: string;
  roomId?: string | null;
  roomName?: string | null;
  studioThumbnailUrl?: string | null;
  date: string;
  startTime: string;
  endTime: string;
  status: string;
  totalPriceWon: number;
  paidAmount?: number | null;
  refundAmount?: number | null;
  refundedAt?: string | null;
  cancelledAt?: string | null;
  createdAt: string;
};

export type SpaceAvailabilitySlot = {
  startTime: string;
  endTime: string;
  bookable: boolean;
  priceWon: number;
};

export type SpaceAvailabilityResponse = {
  date: string;
  slots: SpaceAvailabilitySlot[];
};

export type CreateReviewRequest = {
  studioId: string;
  bookingId: string;
  rating: number;
  content: string;
  imageRefs?: string[];
};

export type ReviewResponse = {
  reviewId: string;
  userId: string;
  studioId: string;
  bookingId: string | null;
  rating: number;
  content: string;
  imageRefs: string[];
  createdAt: string;
};

export type CursorPageResponse<T> = {
  items: T[];
  nextCursor: string | null;
  hasNext: boolean;
  totalCount: number | null;
  size: number;
};

export type RefundEstimateResponse = {
  bookingId: string;
  originalAmount?: number;
  refundRate?: number;
  totalPrice?: number;
  refundRatePercent?: number;
  cancellationFee: number;
  refundAmount: number;
  cancellable: boolean;
  quoteId?: string | null;
  expiresAt?: string | null;
};

// --- Reservation checkout (payment saga) types ---

export type ReservationCheckoutState =
  | 'STARTING'
  | 'SLOT_HELD'
  | 'BOOKING_MODIFYING'
  | 'BOOKING_MODIFY_FAILED'
  | 'COUPON_APPLYING'
  | 'COUPON_HELD'
  | 'COUPON_FAILED'
  | 'PRE_PAYMENT_CHECKING'
  | 'PRE_PAYMENT_READY'
  | 'PRE_PAYMENT_CHECK_FAILED'
  | 'BOOKING_CREATING'
  | 'BOOKING_READY'
  | 'PAYMENT_CREATING'
  | 'PAYMENT_READY'
  | 'PAYMENT_CONFIRMING'
  | 'PAYMENT_APPROVED'
  | 'PAYMENT_FAILED'
  | 'FINALIZING'
  | 'COMPLETED'
  | 'CANCELLING'
  | 'EXPIRED';

export type ReservationCheckoutItemRef = {
  itemId: string;
  quantity?: number;
};

export type StartReservationCheckoutRequest = {
  roomId: string;
  startsAt: string;
  endsAt: string;
  items?: ReservationCheckoutItemRef[];
  basePriceWon?: number;
  reservationAnswers?: ReservationAnswerRequest[];
};

export type StartReservationCheckoutResponse = {
  reservationId: string;
  businessDeadlineAt: string;
};

export type ReservationCheckoutSession = {
  checkoutId: string;
  userId: string;
  state: ReservationCheckoutState;
  revision: number;
  roomId: string;
  startsAt: string;
  endsAt: string;
  basePriceWon: number | null;
  appliedAmountWon: number | null;
  businessDeadlineAt: string | null;
  bookingReservationId: string | null;
  bookingVersion: number | null;
  bookingId: number | null;
  bookingOrderId: string | null;
  bookingFailureReason: string | null;
  prePaymentFailureReason: string | null;
  prePaymentCheckedAt: string | null;
  prePaymentPointVerifiedAt: string | null;
  paymentId: number | null;
  paymentOrderId: string | null;
  paymentAmountWon: number | null;
  paymentStartedAt: string | null;
  paymentMethod: string | null;
  paymentApprovedAt: string | null;
  paymentFailureReason: string | null;
  finalizationStartedAt: string | null;
  bookingPaidAt: string | null;
  roomTimeSlotConfirmedAt: string | null;
  couponConfirmedAt: string | null;
  pointConfirmedAt: string | null;
  reservationCompletedAt: string | null;
  couponState: string | null;
  couponOwnedId: string | number | null;
  pendingCouponOwnedId: string | number | null;
  discountAmountWon: number | null;
  couponFailureReason: string | null;
};

export type ApplyReservationCouponRequest = {
  couponOwnedId: string;
  expectedRevision: number;
};

export type ModifyReservationBookingRequest = {
  roomId: string;
  startsAt: string;
  endsAt: string;
  basePriceWon: number;
  items?: ReservationCheckoutItemRef[];
  expectedRevision?: number;
};

export type StartCheckoutStepRequest = {
  expectedRevision: number;
};

export type ConfirmReservationPaymentRequest = {
  paymentKey: string;
  orderId: string;
  amount: number;
  amountWon?: number;
  expectedRevision: number;
};

// --- Functions ---

export function cancelBooking(bookingId: number | string, req: CancelBookingRequest) {
  return postJson<BookingCommandResponse>(`/api/v1/bookings/${bookingId}/cancel`, req);
}

export function confirmPayment(bookingId: number | string, req: ConfirmPaymentRequest) {
  return postJson<BookingCommandResponse>(`/api/v1/bookings/${bookingId}/confirm-payment`, req);
}

function idempotencyHeaders(idempotencyKey?: string): HeadersInit | undefined {
  return idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined;
}

export function startReservationCheckout(req: StartReservationCheckoutRequest) {
  return postJson<StartReservationCheckoutResponse>('/api/v1/reservations/start', req);
}

export function getReservationCheckout(checkoutId: string, options?: { signal?: AbortSignal }) {
  return getJson<ReservationCheckoutSession>(
    `/api/v1/reservations/${encodeURIComponent(checkoutId)}`,
    options
  );
}

export function applyReservationCoupon(
  checkoutId: string,
  req: ApplyReservationCouponRequest,
  idempotencyKey?: string,
) {
  return requestJson<ReservationCheckoutSession>(`/api/v1/reservations/${encodeURIComponent(checkoutId)}/coupon`, {
    body: JSON.stringify(req),
    headers: idempotencyHeaders(idempotencyKey),
    method: 'POST',
  });
}

/** PATCH /api/v1/reservations/{checkoutId}/booking — 체크아웃 중 예약 시간/옵션 변경. */
export function modifyReservationBooking(
  checkoutId: string,
  req: ModifyReservationBookingRequest,
  idempotencyKey?: string,
) {
  return requestJson<ReservationCheckoutSession>(
    `/api/v1/reservations/${encodeURIComponent(checkoutId)}/booking`,
    {
      body: JSON.stringify(req),
      headers: idempotencyHeaders(idempotencyKey),
      method: 'PATCH',
    }
  );
}

/** DELETE /api/v1/reservations/{checkoutId}/coupon — 적용 쿠폰 해제. */
export function removeReservationCoupon(
  checkoutId: string,
  expectedRevision: number,
  idempotencyKey?: string,
) {
  const query = new URLSearchParams({ expectedRevision: String(expectedRevision) });
  return requestJson<ReservationCheckoutSession>(
    `/api/v1/reservations/${encodeURIComponent(checkoutId)}/coupon?${query.toString()}`,
    {
      headers: idempotencyHeaders(idempotencyKey),
      method: 'DELETE',
    }
  );
}

export function startReservationPrePaymentCheck(
  checkoutId: string,
  req: StartCheckoutStepRequest,
  idempotencyKey?: string,
) {
  return requestJson<ReservationCheckoutSession>(
    `/api/v1/reservations/${encodeURIComponent(checkoutId)}/pre-payment-check`,
    {
      body: JSON.stringify(req),
      headers: idempotencyHeaders(idempotencyKey),
      method: 'POST',
    }
  );
}

export function startReservationPayment(
  checkoutId: string,
  req: StartCheckoutStepRequest,
  idempotencyKey?: string,
) {
  return requestJson<ReservationCheckoutSession>(`/api/v1/reservations/${encodeURIComponent(checkoutId)}/payment`, {
    body: JSON.stringify(req),
    headers: idempotencyHeaders(idempotencyKey),
    method: 'POST',
  });
}

export function confirmReservationPayment(
  checkoutId: string,
  req: ConfirmReservationPaymentRequest,
  idempotencyKey?: string,
) {
  return requestJson<ReservationCheckoutSession>(
    `/api/v1/reservations/${encodeURIComponent(checkoutId)}/payment/confirm`,
    {
      body: JSON.stringify(req),
      headers: idempotencyHeaders(idempotencyKey),
      method: 'POST',
    }
  );
}

export function cancelReservationCheckout(
  checkoutId: string,
  expectedRevision: number,
  idempotencyKey?: string,
) {
  const query = new URLSearchParams({ expectedRevision: String(expectedRevision) });
  return requestJson<ReservationCheckoutSession>(
    `/api/v1/reservations/${encodeURIComponent(checkoutId)}?${query.toString()}`,
    {
      headers: idempotencyHeaders(idempotencyKey),
      method: 'DELETE',
    }
  );
}

export function getBookingDetail(bookingId: number | string) {
  return getJson<BookingDetailResponse>(`/api/v1/bookings/${bookingId}`);
}

export function getMyBookings(params: { tab: string; cursor?: string; size?: number }) {
  const query = new URLSearchParams({ status: params.tab });
  if (params.cursor) query.set('cursor', params.cursor);
  if (params.size != null) query.set('size', String(params.size));
  return getJson<CursorPageResponse<MyBookingItem>>(`/api/v1/users/me/bookings?${query.toString()}`);
}

export function getRefundEstimate(bookingId: number | string) {
  return getJson<RefundEstimateResponse>(`/api/v1/bookings/${bookingId}/refund-estimate`);
}

/** Quote-based refund request (quote from getRefundEstimate). */
export function requestRefund(bookingId: number | string, quoteId: string) {
  return postJson<BookingCommandResponse>(`/api/v1/bookings/${bookingId}/refund`, { quoteId });
}

export type RoomAvailabilitySlot = {
  startTime: string;
  endTime: string;
  available: boolean;
};

export type RoomAvailabilityResponse = {
  date: string;
  slots: RoomAvailabilitySlot[];
};

export function getRoomAvailability(roomId: number | string, date: string) {
  return getJson<RoomAvailabilityResponse>(`/api/v1/rooms/${roomId}/availability?date=${date}`);
}

export function getSpaceAvailability(spaceId: number | string, date: string) {
  return getJson<SpaceAvailabilityResponse>(`/api/v1/spaces/${spaceId}/availability?date=${date}`);
}

export function createReview(req: CreateReviewRequest) {
  return postJson<ReviewResponse>('/api/v1/reviews', req);
}

export function getMyReviews(params: { cursor?: string; size?: number } = {}) {
  const query = new URLSearchParams();
  if (params.cursor) query.set('cursor', params.cursor);
  if (params.size != null) query.set('size', String(params.size));
  const qs = query.toString();
  return getJson<CursorPageResponse<ReviewResponse>>(`/api/v1/users/me/reviews${qs ? `?${qs}` : ''}`);
}

export function deleteReview(reviewId: string) {
  return requestVoid(`/api/v1/reviews/${encodeURIComponent(reviewId)}`, {
    method: 'DELETE',
  });
}
